import type { Json, Tables } from '@hive/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from './keys';

// The signed-in member's saved views for a module. The database only ever
// returns the caller's own.

export type SavedView = Pick<Tables<'saved_views'>, 'id' | 'name' | 'config' | 'is_default' | 'show_in_bar'>;

export function useSavedViews(module: 'grid') {
  return useQuery({
    queryKey: queryKeys.savedViews(module),
    queryFn: async (): Promise<SavedView[]> => {
      const { data, error } = await supabase()
        .from('saved_views')
        .select('id, name, config, is_default, show_in_bar')
        .eq('module', module)
        .order('name');
      if (error) throw error;
      return data;
    },
  });
}

function useInvalidate(module: string) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: queryKeys.savedViews(module) });
}

export function useCreateView(module: 'grid') {
  const invalidate = useInvalidate(module);
  return useMutation({
    mutationFn: async (view: { name: string; config: Json; is_default: boolean; show_in_bar: boolean }) => {
      const { data, error } = await supabase()
        .from('saved_views')
        .insert({ module, ...view })
        .select('id, name, config, is_default, show_in_bar')
        .single();
      if (error) {
        if (error.code === '23505') throw new Error(`You already have a view called “${view.name.trim()}”.`);
        throw error;
      }
      return data;
    },
    onSettled: invalidate,
  });
}

export function useUpdateView(module: 'grid') {
  const invalidate = useInvalidate(module);
  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: string;
      patch: Partial<Pick<SavedView, 'name' | 'config' | 'is_default' | 'show_in_bar'>>;
    }) => {
      const { error } = await supabase().from('saved_views').update(patch).eq('id', id);
      if (error) throw error;
    },
    onSettled: invalidate,
  });
}

export function useDeleteView(module: 'grid') {
  const invalidate = useInvalidate(module);
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase().from('saved_views').delete().eq('id', id);
      if (error) throw error;
    },
    onSettled: invalidate,
  });
}
